import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const BIOMETRIC_ENABLED_KEY = 'vent_biometric_lock_enabled';

export const useBiometricLock = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isNative, setIsNative] = useState(false);

  useEffect(() => {
    const native = Capacitor.isNativePlatform();
    console.log('[BiometricLock] isNativePlatform:', native);
    setIsNative(native);
    if (!native) return;

    const init = async () => {
      try {
        console.log('[BiometricLock] Importing @capgo/capacitor-native-biometric...');
        const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
        console.log('[BiometricLock] Plugin imported successfully');

        // Check if biometrics are available
        const result = await NativeBiometric.isAvailable();
        console.log('[BiometricLock] isAvailable result:', JSON.stringify(result));
        setIsAvailable(result.isAvailable);

        // Check if user has enabled it
        const { value } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
        const enabled = value === 'true';
        setIsEnabled(enabled);

        // If enabled, lock immediately on mount (app just opened or returned)
        if (enabled && result.isAvailable) {
          setIsLocked(true);
        }
      } catch (error) {
        console.error('[BiometricLock] Init error:', error);
        console.error('[BiometricLock] Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
        setIsAvailable(false);
      }
    };

    init();
  }, []);

  // Listen for app coming back from background
  useEffect(() => {
    if (!isNative || !isEnabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isEnabled) {
        setIsLocked(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isNative, isEnabled]);

  const authenticate = useCallback(async (): Promise<boolean> => {
    if (!Capacitor.isNativePlatform()) return true;

    try {
      const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');

      await NativeBiometric.verifyIdentity({
        reason: 'Unlock Vent',
        title: 'Authenticate',
        subtitle: 'Use Face ID or Touch ID to unlock',
        description: 'Your privacy matters. Verify your identity to access Vent.',
      });

      setIsLocked(false);
      return true;
    } catch (error) {
      console.error('[BiometricLock] Authentication failed:', error);
      return false;
    }
  }, []);

  const toggleBiometric = useCallback(async (enable: boolean) => {
    if (!Capacitor.isNativePlatform()) return;

    if (enable) {
      // Verify biometric works before enabling
      const success = await authenticate();
      if (!success) return;
    }

    try {
      await Preferences.set({ key: BIOMETRIC_ENABLED_KEY, value: String(enable) });
    } catch {
      localStorage.setItem(BIOMETRIC_ENABLED_KEY, String(enable));
    }
    setIsEnabled(enable);
    if (!enable) {
      setIsLocked(false);
      // Also clear saved login credentials when biometric lock is disabled
      try {
        const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
        await NativeBiometric.deleteCredentials({ server: 'com.vent.app' });
        console.log('[BiometricLock] Login credentials cleared on disable');
      } catch {
        // Ignore errors
      }
    }
  }, [authenticate]);

  return {
    isEnabled,
    isLocked,
    isAvailable,
    isNative,
    authenticate,
    toggleBiometric,
  };
};
